import apiClient, { getErrorMessage } from './api';

// Seller API functions
export interface Seller {
  id: string;
  location_id: string;
  name: string;
  mobile: string;
  email?: string;
  address?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface SellerCreate {
  location_id: string;
  name: string;
  mobile: string;
  email?: string;
  address?: string;
}

export interface SellerUpdate {
  name?: string;
  mobile?: string;
  email?: string;
  address?: string;
  is_active?: boolean;
}

// Retail Sale API functions
export interface CartonEntry {
  carton_number?: string;
  pond_id: string;
  species: string;
  quantity_kg: number;
  rate_per_kg: number;
  amount: number;
  use_auto_rate?: boolean; // If true, uses default rate; if false, manual rate
}

export interface MarketExpenseItem {
  description: string;
  amount: number;
}

export interface MarketSpeciesLine {
  species: string;
  dispatched_quantity_kg?: number;
  sold_quantity_kg?: number;
  returned_quantity_kg?: number;
  rate_per_kg?: number;
}

export interface OnFarmSaleCreate {
  location_id: string;
  sale_date: string;
  seller_id: string;
  ponds: string[];
  cartons: CartonEntry[];
  amount_received: number;
  remarks?: string;
}

export interface MarketSaleCreate {
  location_id: string;
  sale_date: string;
  seller_id?: string;
  ponds?: string[];
  species_lines: MarketSpeciesLine[];
  expenses: MarketExpenseItem[];
  remarks?: string;
}

export interface RetailSale {
  id: string;
  location_id: string;
  sale_type: 'on_farm' | 'market';
  sale_date: string;
  seller_id: string;
  seller_name?: string;
  seller_mobile?: string;
  retail_sale_number: string;
  status: 'draft' | 'completed' | 'deposit' | 'cancelled';
  payment_method?: 'cash' | 'bank' | 'upi';
  deposit_slip_image?: string;
  deposit_date?: string;
  deposited_by?: string;
  income_log_id?: string;
  ponds: string[];
  pond_names?: string[];
  // On-Farm specific
  cartons?: CartonEntry[];
  total_amount?: number;
  amount_received?: number;
  amount_due?: number;
  // Market specific
  dispatch_quantity_kg?: number; // legacy single-line fields
  sold_quantity_kg?: number;
  returned_quantity_kg?: number;
  species_lines?: MarketSpeciesLine[];
  expenses?: MarketExpenseItem[];
  total_expenses?: number;
  rate_per_kg?: number; // legacy single-line field
  gross_amount?: number;
  net_amount?: number;
  remarks?: string;
  created_at: string;
  updated_at: string;
}

export interface RetailSaleStatusUpdate {
  status: 'draft' | 'completed' | 'deposit' | 'cancelled';
  payment_method?: 'bank' | 'upi';
  deposit_slip_image?: string;
  deposit_date?: string;
}

export interface OnFarmSaleUpdate {
  sale_date?: string;
  seller_id?: string;
  ponds?: string[];
  cartons?: CartonEntry[];
  amount_received?: number;
  remarks?: string;
  // Market sale fields (multi-species dispatch/sold)
  species_lines?: MarketSpeciesLine[];
  expenses?: MarketExpenseItem[];
  dispatch_quantity_kg?: number; // legacy single-line fields for backward compatibility
  sold_quantity_kg?: number;
  returned_quantity_kg?: number;
  rate_per_kg?: number;
  gross_amount?: number;
  net_amount?: number;
}

// Seller API
export const sellerApi = {
  create: async (data: SellerCreate): Promise<Seller> => {
    try {
      const response = await apiClient.post('/sellers', data);
      return response.data;
    } catch (error: any) {
      throw new Error(getErrorMessage(error, 'Failed to create seller'));
    }
  },

  list: async (locationId: string, search?: string): Promise<Seller[]> => {
    try {
      const params = new URLSearchParams({ location_id: locationId });
      if (search) params.append('search', search);
      const response = await apiClient.get(`/sellers?${params.toString()}`);
      return response.data;
    } catch (error: any) {
      throw new Error(getErrorMessage(error, 'Failed to fetch sellers'));
    }
  },

  get: async (sellerId: string): Promise<Seller> => {
    try {
      const response = await apiClient.get(`/sellers/${sellerId}`);
      return response.data;
    } catch (error: any) {
      throw new Error(getErrorMessage(error, 'Failed to fetch seller'));
    }
  },

  update: async (sellerId: string, data: SellerUpdate): Promise<Seller> => {
    try {
      const response = await apiClient.put(`/sellers/${sellerId}`, data);
      return response.data;
    } catch (error: any) {
      throw new Error(getErrorMessage(error, 'Failed to update seller'));
    }
  },

  delete: async (sellerId: string): Promise<void> => {
    try {
      await apiClient.delete(`/sellers/${sellerId}`);
    } catch (error: any) {
      throw new Error(getErrorMessage(error, 'Failed to delete seller'));
    }
  },
};

// Retail Sale API
export const retailSaleApi = {
  createOnFarm: async (data: OnFarmSaleCreate): Promise<RetailSale> => {
    try {
      const response = await apiClient.post('/retail-sales/on-farm', data);
      return response.data;
    } catch (error: any) {
      throw new Error(getErrorMessage(error, 'Failed to create on-farm sale'));
    }
  },

  createMarket: async (data: MarketSaleCreate): Promise<RetailSale> => {
    try {
      const response = await apiClient.post('/retail-sales/market', data);
      return response.data;
    } catch (error: any) {
      throw new Error(getErrorMessage(error, 'Failed to create market sale'));
    }
  },

  list: async (params: {
    location_id: string;
    status?: string;
    sale_type?: string;
    start_date?: string;
    end_date?: string;
    seller_id?: string;
  }): Promise<RetailSale[]> => {
    try {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value);
        }
      });
      const response = await apiClient.get(`/retail-sales?${queryParams.toString()}`);
      return response.data;
    } catch (error: any) {
      throw new Error(getErrorMessage(error, 'Failed to fetch retail sales'));
    }
  },

  get: async (saleId: string): Promise<RetailSale> => {
    try {
      const response = await apiClient.get(`/retail-sales/${saleId}`);
      return response.data;
    } catch (error: any) {
      throw new Error(getErrorMessage(error, 'Failed to fetch retail sale'));
    }
  },

  updateStatus: async (saleId: string, data: RetailSaleStatusUpdate): Promise<RetailSale> => {
    try {
      const response = await apiClient.put(`/retail-sales/${saleId}/status`, data);
      return response.data;
    } catch (error: any) {
      throw new Error(getErrorMessage(error, 'Failed to update sale status'));
    }
  },

  updateDraft: async (saleId: string, data: OnFarmSaleUpdate): Promise<RetailSale> => {
    try {
      const response = await apiClient.put(`/retail-sales/${saleId}/draft`, data);
      return response.data;
    } catch (error: any) {
      throw new Error(getErrorMessage(error, 'Failed to update draft sale'));
    }
  },

  getLiveDrafts: async (locationId: string): Promise<RetailSale[]> => {
    try {
      const response = await apiClient.get(`/retail-sales/drafts/live?location_id=${locationId}`);
      return response.data;
    } catch (error: any) {
      throw new Error(getErrorMessage(error, 'Failed to fetch live draft sales'));
    }
  },

  delete: async (saleId: string): Promise<void> => {
    try {
      await apiClient.delete(`/retail-sales/${saleId}`);
    } catch (error: any) {
      throw new Error(getErrorMessage(error, 'Failed to delete retail sale'));
    }
  },
};

